import React, { useState, useEffect, useCallback } from "react";
import { MdAdd, MdDelete, MdAccessTime } from "react-icons/md";
import { fetchTimeEntries, logTimeEntry, fetchExpenses, addExpense, deleteExpense, logActivity } from "../../../../hooks/useProjects";
import { supabase } from "../../../../lib/supabaseClient";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", maximumFractionDigits: 0 }).format(n || 0);

const INPUT = {
  padding: "8px 12px", border: "1.5px solid #e0e0ee", borderRadius: "8px",
  fontSize: "13px", outline: "none", background: "#fff", fontFamily: "inherit", width: "100%",
};

export default function TimeSheetPanel({ project, onFinancialsChange }) {
  const [timeEntries, setTimeEntries]   = useState([]);
  const [expenses,    setExpenses]      = useState([]);
  const [currentUser, setCurrentUser]   = useState(null);
  const [hoursInput,  setHoursInput]    = useState("");
  const [weekStart,   setWeekStart]     = useState(new Date().toISOString().split("T")[0]);
  const [hoursDesc,   setHoursDesc]     = useState("");
  const [expDesc,     setExpDesc]       = useState("");
  const [expAmount,   setExpAmount]     = useState("");
  const [savingHours, setSavingHours]   = useState(false);
  const [savingExp,   setSavingExp]     = useState(false);

  // Fetch current user
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        supabase.from("employees").select("id, full_name, hourly_rate, job_title").eq("id", user.id).single()
          .then(({ data }) => setCurrentUser(data || { id: user.id, full_name: user.email, hourly_rate: 50 }));
      }
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!project?.id) return;
    try {
      const [entries, exps] = await Promise.all([
        fetchTimeEntries(project.id),
        fetchExpenses(project.id),
      ]);
      setTimeEntries(entries);
      setExpenses(exps);
      if (onFinancialsChange) {
        // Compute labor cost
        const laborCost = entries.reduce((s, e) => {
          const rate = parseFloat(e.employees?.hourly_rate) || 50;
          return s + (parseFloat(e.hours) || 0) * rate;
        }, 0);
        const extCosts = exps.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
        onFinancialsChange({ laborCost, extCosts });
      }
    } catch (err) {
      console.warn("[TimeSheet] load:", err.message);
    }
  }, [project?.id, onFinancialsChange]);

  useEffect(() => { loadData(); }, [loadData]);

  // Subscribe to realtime
  useEffect(() => {
    if (!project?.id) return;
    const ch = supabase.channel(`timesheets_${project.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "project_time_entries", filter: `project_id=eq.${project.id}` }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "project_expenses",     filter: `project_id=eq.${project.id}` }, loadData)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [project?.id, loadData]);

  const handleLogHours = async (e) => {
    e.preventDefault();
    if (!hoursInput || !currentUser) {
      alert("Please select hours and ensure you are logged in");
      return;
    }
    setSavingHours(true);
    try {
      const result = await logTimeEntry({
        projectId: project.id,
        employeeId: currentUser.id,
        hours: hoursInput,
        weekStart,
        description: hoursDesc
      });
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      await logActivity({ projectId: project.id, employeeId: currentUser.id, actionType: "log_hours", description: `Logged ${hoursInput} hours${hoursDesc ? ` (${hoursDesc})` : ''}` });
      
      setHoursInput("");
      setHoursDesc("");
      await loadData();
    } catch (err) {
      console.error("Error logging hours:", err);
      alert("Error logging hours: " + err.message);
    } finally {
      setSavingHours(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expDesc || !expAmount) return;
    setSavingExp(true);
    try {
      await addExpense({ projectId: project.id, description: expDesc, amount: expAmount });
      await logActivity({ projectId: project.id, employeeId: currentUser?.id, actionType: "add_expense", description: `Added expense ${expDesc} for ${fmt(expAmount)}` });
      setExpDesc(""); setExpAmount("");
      await loadData();
    } catch (err) { alert("Error: " + err.message); }
    finally { setSavingExp(false); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    const exp = expenses.find(e => e.id === id);
    try { 
      await deleteExpense(id); 
      if (exp) await logActivity({ projectId: project.id, employeeId: currentUser?.id, actionType: "delete", description: `Deleted expense ${exp.description}` });
      await loadData(); 
    }
    catch (err) { alert("Error: " + err.message); }
  };

  // Compute totals
  const laborCost = timeEntries.reduce((s, e) => {
    const rate = parseFloat(e.employees?.hourly_rate) || 50;
    return s + (parseFloat(e.hours) || 0) * rate;
  }, 0);
  const totalHours  = timeEntries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  const extCosts    = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalCost   = laborCost + extCosts;
  const foreProfit  = (project?.totalContractValue || 0) - totalCost;
  const profitPct   = project?.totalContractValue > 0 ? ((foreProfit / project.totalContractValue) * 100).toFixed(1) : 0;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px" }}>

      {/* ── LEFT: Time Entries ── */}
      <div>
        <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#1a1a2e", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
          <MdAccessTime size={16} color="#3761E9" /> Time Entries
          <span style={{ marginLeft: "auto", fontSize: "11px", color: "#888" }}>Total: {totalHours}h</span>
        </h3>

        {/* Log Hours Form */}
        <form onSubmit={handleLogHours} style={{ background: "#f7f8ff", borderRadius: "10px", padding: "14px", marginBottom: "14px" }}>
          <p style={{ fontSize: "11px", fontWeight: 700, color: "#3761E9", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Log My Hours</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
            <div>
              <label style={{ fontSize: "10px", color: "#888" }}>Hours</label>
              <input style={INPUT} type="number" step="0.5" min="0.5" value={hoursInput} onChange={e => setHoursInput(e.target.value)} placeholder="e.g. 5" required />
            </div>
            <div>
              <label style={{ fontSize: "10px", color: "#888" }}>Week Start</label>
              <input style={INPUT} type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} />
            </div>
          </div>
          <input style={{ ...INPUT, marginBottom: "8px" }} value={hoursDesc} onChange={e => setHoursDesc(e.target.value)} placeholder="Description (optional)" />
          {currentUser && (
            <p style={{ fontSize: "11px", color: "#888", marginBottom: "6px" }}>
              Your rate: SAR {parseFloat(currentUser.hourly_rate) || 50}/hr
              {hoursInput && ` → Cost: ${fmt((parseFloat(hoursInput) || 0) * (parseFloat(currentUser.hourly_rate) || 50))}`}
            </p>
          )}
          <button type="submit" disabled={savingHours} style={{ width: "100%", padding: "8px", background: "#3761E9", color: "#fff", border: "none", borderRadius: "8px", fontSize: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: savingHours ? 0.7 : 1 }}>
            {savingHours ? "Saving…" : "Log Hours"}
          </button>
        </form>

        {/* Entries List */}
        <div style={{ display: "grid", gap: "6px", maxHeight: "260px", overflowY: "auto" }}>
          {timeEntries.length === 0
            ? <p style={{ fontSize: "12px", color: "#bbb", textAlign: "center", padding: "20px 0" }}>No time entries yet</p>
            : timeEntries.map(e => {
              const rate = parseFloat(e.employees?.hourly_rate) || 50;
              return (
                <div key={e.id} style={{ background: "#fff", border: "1px solid #e8e8f0", borderRadius: "8px", padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ fontSize: "12px", fontWeight: 600, color: "#1a1a2e" }}>
                      {e.employees?.full_name || "Unknown"}
                    </p>
                    <p style={{ fontSize: "10px", color: "#aaa" }}>
                      {e.week_start} · {e.hours}h · Rate: {rate} SAR/hr
                      {e.description ? ` · ${e.description}` : ""}
                    </p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#3761E9", display: "block" }}>{fmt(e.hours * rate)}</span>
                    <button onClick={async () => {
                      if (window.confirm("Delete this time entry?")) {
                        const { error } = await supabase.from("project_time_entries").delete().eq("id", e.id);
                        if (!error) loadData();
                      }
                    }} style={{ background: "none", border: "none", color: "#E53935", cursor: "pointer", fontSize: "10px", marginTop: "4px" }}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>

      {/* ── RIGHT: Expenses + Summary ── */}
      <div>
        <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#1a1a2e", marginBottom: "12px" }}>
          💸 External Costs
        </h3>

        {/* Add Expense Form */}
        <form onSubmit={handleAddExpense} style={{ background: "#FFF8E1", borderRadius: "10px", padding: "14px", marginBottom: "14px" }}>
          <p style={{ fontSize: "11px", fontWeight: 700, color: "#E65100", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Add Expense</p>
          <input style={{ ...INPUT, marginBottom: "8px" }} value={expDesc} onChange={e => setExpDesc(e.target.value)} placeholder="Description (e.g. Travel, Software)" required />
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px" }}>
            <input style={INPUT} type="number" min="1" value={expAmount} onChange={e => setExpAmount(e.target.value)} placeholder="Amount (SAR)" required />
            <button type="submit" disabled={savingExp} style={{ padding: "8px 16px", background: "#FB8C00", color: "#fff", border: "none", borderRadius: "8px", fontSize: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              {savingExp ? "…" : <MdAdd size={18} />}
            </button>
          </div>
        </form>

        {/* Expenses List */}
        <div style={{ display: "grid", gap: "6px", marginBottom: "16px", maxHeight: "160px", overflowY: "auto" }}>
          {expenses.length === 0
            ? <p style={{ fontSize: "12px", color: "#bbb", textAlign: "center", padding: "10px 0" }}>No expenses yet</p>
            : expenses.map(exp => (
              <div key={exp.id} style={{ background: "#fff", border: "1px solid #e8e8f0", borderRadius: "8px", padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p style={{ fontSize: "12px", color: "#444" }}>{exp.description}</p>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "#FB8C00" }}>{fmt(exp.amount)}</span>
                  <button onClick={() => handleDeleteExpense(exp.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#E53935", padding: 0 }}>
                    <MdDelete size={15} />
                  </button>
                </div>
              </div>
            ))
          }
        </div>

        {/* ── Profitability Summary ── */}
        <div style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", borderRadius: "14px", padding: "18px", color: "#fff" }}>
          <p style={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "14px" }}>Profitability Engine</p>
          {[
            { label: "Contract Value", value: fmt(project?.totalContractValue), color: "#a0c4ff" },
            { label: "Labor Cost", value: `- ${fmt(laborCost)}`, color: "#ffb3c6" },
            { label: "External Costs", value: `- ${fmt(extCosts)}`, color: "#ffd166" },
          ].map(row => (
            <div key={row.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
              <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)" }}>{row.label}</span>
              <span style={{ fontSize: "12px", fontWeight: 600, color: row.color }}>{row.value}</span>
            </div>
          ))}
          <div style={{ height: "1px", background: "rgba(255,255,255,0.1)", margin: "10px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>Forecasted Profit</span>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: "18px", fontWeight: 900, color: foreProfit >= 0 ? "#6affb0" : "#ff6b6b" }}>
                {fmt(foreProfit)}
              </span>
              <span style={{ display: "block", fontSize: "10px", color: foreProfit >= 0 ? "#6affb0" : "#ff6b6b" }}>
                {profitPct}% margin
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
